import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sprout, ArrowLeft, MailCheck } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore nella richiesta");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[#FDFBF7] p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-6">
          <ArrowLeft size={13}/> Torna al login
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#1C352D] grid place-items-center text-white">
            <Sprout size={20}/>
          </div>
          <div>
            <div className="font-semibold text-[#1C352D]">RDM ClassRoom</div>
            <div className="text-[10px] text-stone-500 uppercase tracking-[0.25em]">Ramo di Mandorlo · Scuola</div>
          </div>
        </div>

        {sent ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-900" data-testid="forgot-sent">
            <div className="flex items-center gap-2 font-medium mb-2">
              <MailCheck size={18}/> Controlla la tua email
            </div>
            <p>Se l'indirizzo <b>{email}</b> è registrato, riceverai a breve un'email con il link per reimpostare la password. Il link scade dopo <b>1 ora</b>.</p>
            <p className="mt-3 text-[12px] text-emerald-800">
              Non ricevi nulla? Controlla lo spam o contatta la segreteria per un reset manuale.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-[#1C352D]">Password dimenticata?</h1>
            <p className="text-sm text-stone-500 mt-2">Inserisci l'email del tuo account: ti invieremo un link per scegliere una nuova password.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Email</Label>
                <Input
                  id="email" type="email" required
                  data-testid="forgot-email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-white h-11 border-stone-200 focus-visible:ring-[#1C352D]"
                  placeholder="tuo@indirizzo.it"
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
              )}
              <Button
                type="submit" data-testid="forgot-submit"
                disabled={loading || !email}
                className="w-full h-12 rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors"
              >
                {loading ? "Invio in corso…" : "Invia link di reset"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
